import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { type AppCollection, type AppRecord, loadAppList } from '@/lib/app-records';
import { appListToTable } from '@shared/app-import';
import type { ImportColumn } from '@shared/table-import';

export interface AppSourceTable {
  headers: string[];
  rows: string[][];
  mapping: ImportColumn[];
  /** What the import is named after: the app, and the statuses kept. */
  label?: string;
}

interface AppSourcePickerProps {
  /** The app being filled: not offered as its own source. */
  toolAppId: string;
  initialAppKey?: string | null;
  initialStatuses?: string[];
  onTable: (table: AppSourceTable | null) => void;
}

const field = 'w-full rounded-lg border border-domino-cardBorder bg-domino-white px-3 py-2 text-sm text-domino-ink focus:border-domino-aubergine focus:outline-none';

/**
 * « Résultats d'une app » (04/11/2026): choose an app whose list the person can
 * open, keep only some statuses; its objects become the table to import.
 */
export const AppSourcePicker = ({ toolAppId, initialAppKey = null, initialStatuses = [], onTable }: AppSourcePickerProps) => {
  const [apps, setApps] = useState<Array<{ id: string; key: string; title: string }>>([]);
  const [appId, setAppId] = useState<string | null>(null);
  const [list, setList] = useState<{ collection: AppCollection; records: AppRecord[] } | null>(null);
  const [statuses, setStatuses] = useState<string[]>(initialStatuses);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Apps with a list of rows, read under the person's rights.
    void supabase.from('tool_apps').select('id, key, title, app_collections!inner(mode)').eq('app_collections.mode', 'rows').neq('id', toolAppId).order('title')
      .then(({ data, error: readError }) => {
        if (readError) { setError('Les applications n’ont pas pu être chargées.'); return; }
        const found = ((data ?? []) as Array<{ id: string; key: string; title: string }>).map(({ id, key, title }) => ({ id, key, title }));
        setApps(found);
        const initial = found.find((app) => app.key === initialAppKey);
        if (initial) setAppId(initial.id);
      });
  }, [toolAppId, initialAppKey]);

  useEffect(() => {
    if (!appId) { setList(null); return; }
    void loadAppList(appId).then(setList).catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Liste illisible.'));
  }, [appId]);

  const table = useMemo(() => {
    if (!list) return null;
    const converted = appListToTable({
      columns: list.collection.columns.map((column) => ({ key: column.key, label: column.label, role: (column as { role?: string }).role ?? null })),
      objects: list.records.map((record) => ({ status: record.status, archived: Boolean(record.archivedAt), cells: record.computed })),
      statuses,
    });
    const title = apps.find((app) => app.id === appId)?.title ?? '';
    const kept = list.collection.statuses.filter((status) => statuses.includes(status.key)).map((status) => status.label);
    return { ...converted, label: [title, kept.join(', ')].filter(Boolean).join(' · ') };
  }, [list, statuses, apps, appId]);
  useEffect(() => { onTable(table); }, [table, onTable]);

  const counts = useMemo(() => {
    const byStatus = new Map<string, number>();
    for (const record of list?.records ?? []) if (!record.archivedAt) byStatus.set(record.status, (byStatus.get(record.status) ?? 0) + 1);
    return byStatus;
  }, [list]);

  return (
    <div className="space-y-3">
      {error && <p role="alert" className="text-sm text-domino-inkSoft">{error}</p>}
      <select value={appId ?? ''} onChange={(event) => { setAppId(event.target.value || null); setStatuses([]); }} aria-label="Application source" className={field}>
        <option value="">Choisir une application</option>
        {apps.map((app) => <option key={app.id} value={app.id}>{app.title}</option>)}
      </select>
      {list && (
        <div className="flex flex-wrap gap-1.5">
          {list.collection.statuses.filter((status) => counts.get(status.key)).map((status) => {
            const on = statuses.includes(status.key);
            return (
              <button key={status.key} type="button" aria-pressed={on}
                onClick={() => setStatuses(on ? statuses.filter((key) => key !== status.key) : [...statuses, status.key])}
                className={on ? 'rounded-lg bg-domino-aubergine px-3 py-1.5 text-xs font-semibold text-white' : 'rounded-lg border border-domino-cardBorder px-3 py-1.5 text-xs text-domino-inkSoft'}>
                {status.label} · {counts.get(status.key)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
